import {
  Dropdown,
  FormLabel,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';
import MonthPicker from 'components/month-picker/month-picker';
import QuarterPicker from 'components/quarter-picker/quarter-picker';
import YearPicker from 'components/year-picker/year-picker';
import { displaySettingsAtom } from './atoms';
import type { AggregationKey, DisplaySettings } from './types';
import styles from './output-display-controls.module.scss';

/** Time-interval options + their display labels. */
const AGGREGATION_ITEMS: AggregationKey[] = ['monthly', 'quarterly', 'yearly'];
const AGGREGATION_LABEL: Record<AggregationKey, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};
/** The plural noun for the relative-range labels ("{noun} Before Effective Date"). */
const AGGREGATION_NOUN: Record<AggregationKey, string> = {
  monthly: 'Months',
  quarterly: 'Quarters',
  yearly: 'Years',
};

/**
 * The output display controls (mock §9, in the Eval Settings panel): Time Interval
 * (the one `eval_result` time input), Measurement Basis (net/gross), and the Date
 * Range (relative offsets or an absolute min/max, with the absolute picker swapping
 * Month / Quarter / Year by the active aggregation). Purely atom-backed — writes
 * per-field into `displaySettingsAtom`; it does NOT touch the working-config form, so
 * editing these never dirties Calculate.
 *
 * Decided (lead): all three are INERT over Package Overview (its `Pricing` rollup has
 * no net/gross dimension and is aggregation-invariant), but shipped wired so the
 * deferred Cash Flow / Production Volumes sections drop in working — `aggregation`
 * already feeds `useEvalResult`; measurement + date range have no P5 reader yet.
 *
 * @return {JSX.Element} The display-controls panel section.
 */
export function OutputDisplayControls() {
  const [settings, setSettings] = useAtom(displaySettingsAtom);
  const { aggregation, measurementType, dateRange } = settings;
  const noun = AGGREGATION_NOUN[aggregation];

  const setAggregation = (value: AggregationKey) =>
    setSettings((prev) => ({ ...prev, aggregation: value }));

  const setMeasurement = (value: DisplaySettings['measurementType']) =>
    setSettings((prev) => ({ ...prev, measurementType: value }));

  const setMode = (mode: DisplaySettings['dateRange']['mode']) =>
    setSettings((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, mode },
    }));

  const setRelative = (key: 'pre' | 'post', value: number) =>
    setSettings((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        relative: { ...prev.dateRange.relative, [key]: value },
      },
    }));

  const setAbsolute = (key: 'min' | 'max', value: string) =>
    setSettings((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        absolute: { ...prev.dateRange.absolute, [key]: value },
      },
    }));

  /**
   * The absolute date picker for one bound — Month / Quarter / Year by aggregation.
   * Stores the picked period's start as a `yyyy-MM-dd` string (the resolver snaps it
   * to the period via `startOf`/`endOf`).
   *
   * @param {'min' | 'max'} bound - Which absolute bound this picker edits.
   * @return {JSX.Element} The period picker for the active aggregation.
   */
  const renderAbsolutePicker = (bound: 'min' | 'max') => {
    const stored =
      bound === 'min' ? dateRange.absolute.min : dateRange.absolute.max;
    const selected = parseMomentUtc(stored ?? undefined);
    const labelPrefix = bound === 'min' ? 'Min' : 'Max';

    if (aggregation === 'monthly') {
      return (
        <MonthPicker
          light
          label={`${labelPrefix} Month`}
          selectedMonth={selected}
          setSelectedMonth={(month, year) =>
            setAbsolute(
              bound,
              parseMomentUtc(`${year}-01-01`)
                .month(month)
                .format(SERVER_DATE_FORMAT)
            )
          }
        />
      );
    }
    if (aggregation === 'quarterly') {
      return (
        <QuarterPicker
          light
          label={`${labelPrefix} Quarter`}
          selectedQuarter={selected}
          setSelectedQuarter={(quarter, year) =>
            setAbsolute(
              bound,
              parseMomentUtc(`${year}-01-01`)
                .quarter(quarter)
                .startOf('quarter')
                .format(SERVER_DATE_FORMAT)
            )
          }
        />
      );
    }
    return (
      <YearPicker
        light
        label={`${labelPrefix} Year`}
        selectedYear={selected}
        setSelectedYear={(year) =>
          setAbsolute(
            bound,
            parseMomentUtc(`${year}-01-01`).format(SERVER_DATE_FORMAT)
          )
        }
      />
    );
  };

  return (
    <div className={styles.controls}>
      {/* Order mirrors the mock: Measurement Basis → Range Type → Time Interval. The
          mock pairs Time Interval with Effective Date; with Time Interval trailing here
          and EvalSettingsForm (Effective Date first) rendered next, the two sit adjacent
          across the panel seam. */}
      <div className={styles.field}>
        <FormLabel>Measurement Basis</FormLabel>
        <RadioButtonGroup
          name="eval3-measurement"
          valueSelected={measurementType}
          onChange={(value) =>
            setMeasurement(value as DisplaySettings['measurementType'])
          }
        >
          <RadioButton labelText="Net" value="net" id="measurement-net" />
          <RadioButton labelText="Gross" value="gross" id="measurement-gross" />
        </RadioButtonGroup>
      </div>

      <div className={styles.field}>
        <FormLabel>Range Type</FormLabel>
        <RadioButtonGroup
          name="eval3-range-mode"
          valueSelected={dateRange.mode}
          onChange={(value) =>
            setMode(value as DisplaySettings['dateRange']['mode'])
          }
        >
          <RadioButton
            labelText="Relative"
            value="relative"
            id="range-relative"
          />
          <RadioButton
            labelText="Absolute"
            value="absolute"
            id="range-absolute"
          />
        </RadioButtonGroup>

        {dateRange.mode === 'relative' ? (
          <div className={styles.row}>
            <NumberInput
              id="eval3-range-pre"
              label={`${noun} Before Effective Date`}
              min={0}
              size="sm"
              value={dateRange.relative.pre}
              onChange={(_event, { value }) => {
                // Carbon emits '' on a cleared field; don't snap the offset to 0.
                if (value !== '') {
                  setRelative('pre', Number(value));
                }
              }}
            />
            <NumberInput
              id="eval3-range-post"
              label={`${noun} After Effective Date`}
              min={0}
              size="sm"
              value={dateRange.relative.post}
              onChange={(_event, { value }) => {
                // Carbon emits '' on a cleared field; don't snap the offset to 0.
                if (value !== '') {
                  setRelative('post', Number(value));
                }
              }}
            />
          </div>
        ) : (
          <div className={styles.row}>
            {renderAbsolutePicker('min')}
            {renderAbsolutePicker('max')}
          </div>
        )}
      </div>

      <Dropdown<AggregationKey>
        id="eval3-new-time-interval"
        titleText="Time Interval"
        label="Select interval"
        items={AGGREGATION_ITEMS}
        itemToString={(item) => (item ? AGGREGATION_LABEL[item] : '')}
        selectedItem={aggregation}
        onChange={({ selectedItem }) => {
          if (selectedItem) {
            setAggregation(selectedItem);
          }
        }}
        size="sm"
      />
    </div>
  );
}
